import {
	PATIENT_EDUCATION_LEVEL_LABELS,
	PATIENT_MARITAL_STATUS_LABELS,
} from '@/constants/patient';
import { formatPbtsForPrint } from '@/utils/pbts';
import { formatDateDisplay } from '@/utils/participantDetailDisplay';
import { resolveProntuarioProfileHeroMeta } from '@/pages/professional/sessao-chamada/utils/resolveProntuarioProfileHeroMeta';
import { buildSummaryFromSession } from '@/pages/professional/sessao-chamada/utils/prontuarioPatientSummary';
import type {
	ProntuarioSessionPrintField,
	ProntuarioSessionPrintPatient,
} from './prontuarioSessionPrint';

const ATTENDANCE_LABELS: Record<string, string> = {
	PRESENT: 'Presente',
	ABSENT_JUSTIFIED: 'Faltou (justificado)',
	ABSENT_UNJUSTIFIED: 'Faltou (sem justificativa)',
};

const RISK_LABELS: Record<string, string> = {
	NONE: 'Nenhum',
	LOW: 'Baixo',
	MODERATE: 'Moderado',
	HIGH: 'Alto',
};

export type ProntuarioSessionPrintSource = Pick<
	MedicalRecords.MedicalRecordSession,
	| 'attendance'
	| 'risk_indicator'
	| 'summary'
	| 'summary_tag'
	| 'observed_mood'
	| 'observed_mood_tag'
	| 'secondary_complaints'
	| 'secondary_complaints_tag'
	| 'content_and_techniques'
	| 'content_and_techniques_tag'
	| 'evolution_notes'
	| 'evolution_notes_tag'
	| 'risk_notes'
	| 'risk_notes_tag'
	| 'raw_notes'
	| 'raw_notes_tag'
>;

const PRINT_FIELD_EMPTY = '—';

const buildTaggedPrintField = (
	label: string,
	value: string | null | undefined,
	tag?: string | null,
): ProntuarioSessionPrintField => {
	const text = value?.trim() ?? '';
	const formattedTags = formatPbtsForPrint(tag)?.trim() ?? '';

	if (!text && !formattedTags) {
		return { label, value: PRINT_FIELD_EMPTY };
	}

	return {
		label,
		value: text,
		...(formattedTags ? { tags: formattedTags } : {}),
	};
};

export const buildProntuarioSessionPrintFields = (
	source: ProntuarioSessionPrintSource,
): ProntuarioSessionPrintField[] => {
	const attendance = source.attendance ?? 'PRESENT';
	const riskIndicator = source.risk_indicator ?? 'NONE';

	const fields: ProntuarioSessionPrintField[] = [
		{
			label: 'Presença',
			value: ATTENDANCE_LABELS[attendance] ?? attendance,
		},
		{
			label: 'Indicador de risco',
			value: RISK_LABELS[riskIndicator] ?? riskIndicator,
		},
	];

	const summary = source.summary?.trim();
	if (summary) {
		fields.push(
			buildTaggedPrintField('Resumo gerado', summary, source.summary_tag),
		);
	}

	fields.push(
		buildTaggedPrintField(
			'Humor observado',
			source.observed_mood,
			source.observed_mood_tag,
		),
		buildTaggedPrintField(
			'Queixas secundárias',
			source.secondary_complaints,
			source.secondary_complaints_tag,
		),
		buildTaggedPrintField(
			'Conteúdo e técnicas aplicadas',
			source.content_and_techniques,
			source.content_and_techniques_tag,
		),
		buildTaggedPrintField(
			'Evolução e observações',
			source.evolution_notes,
			source.evolution_notes_tag,
		),
	);

	if (riskIndicator !== 'NONE') {
		fields.push(
			buildTaggedPrintField(
				'Observações de risco',
				source.risk_notes,
				source.risk_notes_tag,
			),
		);
	}

	fields.push(
		buildTaggedPrintField(
			'Anotações livres',
			source.raw_notes,
			source.raw_notes_tag,
		),
	);

	return fields;
};

export const buildProntuarioSessionPrintPatientData = (
	patient: Patients.Patient | null | undefined,
	session: Sessions.Session,
): ProntuarioSessionPrintPatient => {
	const profileMeta = resolveProntuarioProfileHeroMeta(
		patient,
		null,
		session,
	);
	const fallbackName = buildSummaryFromSession(session).fullName;

	return {
		fullName: patient?.full_name?.trim() || fallbackName,
		cpf: profileMeta.cpf,
		rg: patient?.rg,
		birthDate: patient?.birth_date
			? formatDateDisplay(patient.birth_date)
			: null,
		gender: profileMeta.gender,
		maritalStatus: patient?.marital_status
			? PATIENT_MARITAL_STATUS_LABELS[patient.marital_status]
			: null,
		educationLevel: patient?.education_level
			? PATIENT_EDUCATION_LEVEL_LABELS[patient.education_level]
			: null,
		profession: patient?.profession,
		phone: patient?.phone,
		email: patient?.email,
		emergencyContact: patient?.emergency_contact,
	};
};
